import { Chip } from '@material-ui/core';
import React, { useImperativeHandle, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  askGenie,
  IGenieAttachment,
  IGenieQueryResult,
  IGenieResponse,
} from '~/src/dash/apiV2/genie';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  Col,
  Cols,
  Div,
  Row,
  Rows,
  Span,
  Text,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { graphColors, publicaColors } from '~/src/dash/utils/colors';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';

export const GENIE_DEFAULT_PUBLISHER_ID = 381;

const chartColors = [
  graphColors.cyan,
  graphColors.forestGreen,
  graphColors.orange,
  graphColors.purpleBlue,
  graphColors.redOrange,
  graphColors.mustard,
  graphColors.coralPink,
  graphColors.seaGreen,
  graphColors.darkBlue,
  graphColors.limeGreen,
];

interface IChartDataPoint {
  name: string;
  value: number;
}

interface IContextMetric {
  name: string;
  value: number;
}

interface IContextEntry {
  label: string;
  fullName: string;
  metrics: IContextMetric[];
}

const parseMarkdownTable = (text: string): IChartDataPoint[] => {
  const lines = text.split('\n').map((l) => l.trim());
  const tableStart = lines.findIndex(
    (l) => l.startsWith('|') && l.endsWith('|') && l.split('|').length >= 3,
  );
  if (tableStart === -1) return [];

  const headerLine = lines[tableStart];
  const headers = headerLine
    .split('|')
    .map((h) => h.trim())
    .filter(Boolean);
  if (headers.length < 2) return [];

  const separatorIdx = tableStart + 1;
  if (separatorIdx >= lines.length || !/^[\s|:-]+$/.test(lines[separatorIdx])) return [];

  const numericColIdx = headers.findIndex((_, i) => {
    const firstDataRow = lines[separatorIdx + 1];
    if (!firstDataRow) return false;
    const cells = firstDataRow
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean);
    return cells[i] && /^[\d,]+\.?\d*$/.test(cells[i].replace(/,/g, ''));
  });
  if (numericColIdx === -1) return [];

  const labelColIdx = numericColIdx === 0 ? 1 : 0;
  const dataPoints: IChartDataPoint[] = [];

  for (let i = separatorIdx + 1; i < lines.length; i++) {
    const row = lines[i];
    if (!row.startsWith('|') || !row.endsWith('|')) break;

    const cells = row
      .split('|')
      .map((c) => c.replace(/\*\*/g, '').trim())
      .filter(Boolean);
    if (cells.length < 2) break;

    const name = cells[labelColIdx];
    const value = parseFloat((cells[numericColIdx] || '').replace(/,/g, ''));
    if (name && !isNaN(value)) {
      dataPoints.push({ name, value });
    }
  }

  return dataPoints;
};

const parseChartData = (text: string): IChartDataPoint[] => {
  const tableData = parseMarkdownTable(text);
  if (tableData.length >= 2) return tableData;

  const lines = text.split('\n');
  const dataPoints: IChartDataPoint[] = [];

  for (const line of lines) {
    const cleaned = line
      .replace(/^\s*[-*•]\s+/, '')
      .replace(/^\s*\d+\.\s+/, '')
      .replace(/\*\*/g, '')
      .replace(/`/g, '')
      .trim();

    const match = cleaned.match(/^([a-zA-Z0-9_\s./-]+?):\s*([\d,]+\.?\d*)\s*$/);
    if (match) {
      const name = match[1].trim();
      const value = parseFloat(match[2].replace(/,/g, ''));
      if (!isNaN(value) && name.length > 0 && name.length < 60) {
        dataPoints.push({ name, value });
      }
    }
  }

  return dataPoints.length >= 2 ? dataPoints : [];
};

const parseMetrics = (text: string): IContextMetric[] => {
  const metrics: IContextMetric[] = [];

  // Pattern 1: $value metric_name (e.g., "$1,789.91 total revenue")
  const dollarPattern = /\$([\d,]+\.?\d*)\s+([a-zA-Z][a-zA-Z\s]*[a-zA-Z])/g;
  let dollarMatch;
  while ((dollarMatch = dollarPattern.exec(text)) !== null) {
    const value = parseFloat(dollarMatch[1].replace(/,/g, ''));
    const name = dollarMatch[2].trim();
    if (!isNaN(value) && name.length > 0) {
      metrics.push({ name, value });
    }
  }
  if (metrics.length >= 2) return metrics;

  // Pattern 2: metric_name value (e.g., "weighted avg revenue per request 29.02")
  // Smart split: only split on commas followed by a letter or $, not digits (avoids breaking "1,789")
  const segments = text.split(/,\s+(?=[a-zA-Z$])/);
  for (const seg of segments) {
    const trimmed = seg.trim();
    const match = trimmed.match(/^(.+?)\s+([\d,]+\.?\d*)\s*$/);
    if (match) {
      const name = match[1].trim();
      const value = parseFloat(match[2].replace(/,/g, ''));
      if (!isNaN(value) && name.length > 0) {
        metrics.push({ name, value });
      }
    }
  }
  return metrics;
};

const parseContextData = (text: string): IContextEntry[] => {
  const lines = text.split('\n');
  const entries: IContextEntry[] = [];

  for (const line of lines) {
    const cleaned = line
      .replace(/^\s*[-*•]\s+/, '')
      .replace(/^\s*\d+\.\s+/, '')
      .replace(/\*\*/g, '')
      .replace(/`/g, '')
      .trim();

    const colonMatches = [...cleaned.matchAll(/:\s/g)];
    if (colonMatches.length === 0) continue;

    let bestSplit = -1;
    let bestMetrics: IContextMetric[] = [];

    for (const m of colonMatches) {
      const idx = m.index!;
      const metricsPart = cleaned.substring(idx + 2);
      const metrics = parseMetrics(metricsPart);
      if (metrics.length >= 2 && metrics.length > bestMetrics.length) {
        bestSplit = idx;
        bestMetrics = metrics;
      }
    }

    if (bestSplit === -1 || bestMetrics.length < 2) continue;

    const contextPart = cleaned.substring(0, bestSplit).trim();
    if (contextPart.length < 3) continue;

    const parenMatch = contextPart.match(/\(([^)]+)\)/);
    let label: string;
    if (parenMatch) {
      label = parenMatch[1].trim();
    } else {
      const parts = contextPart.split(',').map((p) => p.trim());
      if (parts.length > 3) {
        label = `${parts[1]}, ${parts[3]}`;
      } else if (parts.length > 1) {
        label = parts.slice(0, 2).join(', ');
      } else {
        label = contextPart;
      }
    }
    if (label.length > 40) {
      label = label.substring(0, 37) + '...';
    }

    entries.push({ label, fullName: contextPart, metrics: bestMetrics });
  }

  return entries;
};

const parseQueryResultChart = (queryResult: IGenieQueryResult): IChartDataPoint[] => {
  const { columns, data_array: dataArray } = queryResult;
  if (!columns?.length || !dataArray?.length) return [];

  const labelColIdx = columns.findIndex(
    (c) =>
      c.type_name === 'STRING' || c.name === 'context' || c.name === 'name' || c.name === 'label',
  );
  const valueColIdx = columns.findIndex(
    (c) =>
      c.type_name === 'DOUBLE' ||
      c.type_name === 'FLOAT' ||
      c.type_name === 'DECIMAL' ||
      c.type_name === 'LONG' ||
      c.type_name === 'INT' ||
      c.name === 'total_revenue' ||
      c.name === 'value' ||
      c.name === 'resp_cpm',
  );

  if (labelColIdx === -1 || valueColIdx === -1) {
    const firstCell = dataArray[0]?.[0];
    if (firstCell?.startsWith('[')) {
      try {
        const parsed = JSON.parse(firstCell) as Array<Record<string, unknown>>;
        return parsed
          .filter((item) => {
            const keys = Object.keys(item);
            return keys.length >= 2;
          })
          .map((item) => {
            const keys = Object.keys(item);
            const nameKey = keys.find((k) => typeof item[k] === 'string') || keys[0];
            const valueKey = keys.find((k) => typeof item[k] === 'number') || keys[1];
            return {
              name: String(item[nameKey]),
              value: Number(item[valueKey]) || 0,
            };
          });
      } catch {
        return [];
      }
    }
    return [];
  }

  return dataArray
    .map((row) => ({
      name: String(row[labelColIdx] || ''),
      value: parseFloat(row[valueColIdx]) || 0,
    }))
    .filter((d) => d.name && d.value > 0);
};

interface IQueryResultItem {
  context: string;
  [key: string]: unknown;
}

const parseQueryResultItems = (queryResult: IGenieQueryResult): IQueryResultItem[] => {
  const { data_array: dataArray } = queryResult;
  if (!dataArray?.length) return [];

  const firstCell = dataArray[0]?.[0];
  if (firstCell?.startsWith('[')) {
    try {
      return JSON.parse(firstCell) as IQueryResultItem[];
    } catch {
      return [];
    }
  }
  return [];
};

const extractDimensionNames = (sql: string): string[] => {
  const groupByMatch = sql.match(
    /GROUP\s+BY\s+(.+?)(?:\s+ORDER|\s+HAVING|\s+LIMIT|\s*;|\s*--|\s*\))/i,
  );
  if (!groupByMatch) return [];
  return groupByMatch[1]
    .split(',')
    .map((s) => s.replace(/`/g, '').trim())
    .filter(Boolean);
};

const formatDimensionName = (name: string): string =>
  name
    .replace(/`/g, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getValueFromItem = (item: IQueryResultItem): number => {
  const keys = Object.keys(item);
  const valueKey = keys.find((k) => k !== 'context' && typeof item[k] === 'number');
  return valueKey ? Number(item[valueKey]) || 0 : 0;
};

const GenieChart = ({ data }: { data: IChartDataPoint[] }): React.ReactNode => {
  const { t } = usePublicaTranslation();
  if (data.length === 0) return null;

  const maxLabelLen = Math.max(...data.map((d) => d.name.length));
  const leftMargin = Math.min(maxLabelLen * 6, 120);

  return (
    <Div style={{ marginTop: '12px' }}>
      <ResponsiveContainer width='100%' height={data.length * 36 + 40}>
        <BarChart
          data={data}
          layout='vertical'
          margin={{ top: 4, right: 20, left: leftMargin, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray='3 3' horizontal={false} />
          <XAxis type='number' tick={{ fontSize: 11 }} />
          <YAxis
            type='category'
            dataKey='name'
            tick={{ fontSize: 11, fill: publicaColors.darkGray }}
            width={leftMargin}
          />
          <Tooltip
            contentStyle={{
              fontSize: '12px',
              borderRadius: '6px',
              border: `1px solid ${publicaColors.lightGray}`,
            }}
            formatter={(value: number) => [value.toFixed(2), t('Value')]}
          />
          <Bar dataKey='value' radius={[0, 4, 4, 0]} barSize={20}>
            {data.map((_, idx) => (
              <Cell key={idx} fill={chartColors[idx % chartColors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Div>
  );
};

const GenieContextChart = ({
  entries,
}: {
  entries: IContextEntry[];
}): React.ReactElement | null => {
  const [selectedIdx, setSelectedIdx] = useState(0);

  if (entries.length === 0) return null;

  const selected = entries[selectedIdx];
  const chartData: IChartDataPoint[] = selected.metrics.map((m) => ({
    name: m.name,
    value: m.value,
  }));
  const maxLabelLen = Math.max(...chartData.map((d) => d.name.length));
  const leftMargin = Math.min(maxLabelLen * 6.5, 200);

  return (
    <Rows style={{ marginTop: '12px' }}>
      {entries.length > 1 && (
        <Row noPadding marginBottom='8px' displayFlex style={{ flexWrap: 'wrap', gap: '6px' }}>
          {entries.map((entry, idx) => (
            <Chip
              key={idx}
              size='small'
              label={entry.label}
              onClick={() => setSelectedIdx(idx)}
              clickable
              variant={idx === selectedIdx ? 'default' : 'outlined'}
              color={idx === selectedIdx ? 'primary' : 'default'}
              style={{ fontSize: '11px' }}
            />
          ))}
        </Row>
      )}
      <Row noPadding marginBottom='4px'>
        <Span style={{ fontSize: '11px', color: publicaColors.mediumGray }}>
          {selected.fullName}
        </Span>
      </Row>
      <Row noPadding>
        <ResponsiveContainer width='100%' height={chartData.length * 36 + 40}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 4, right: 20, left: leftMargin, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={false} />
            <XAxis type='number' tick={{ fontSize: 11 }} />
            <YAxis
              type='category'
              dataKey='name'
              tick={{ fontSize: 11, fill: publicaColors.darkGray }}
              width={leftMargin}
            />
            <Tooltip
              contentStyle={{
                fontSize: '12px',
                borderRadius: '6px',
                border: `1px solid ${publicaColors.lightGray}`,
              }}
              formatter={(value: number) => [value.toFixed(2), '']}
            />
            <Bar dataKey='value' radius={[0, 4, 4, 0]} barSize={20}>
              {chartData.map((_, idx) => (
                <Cell key={idx} fill={chartColors[idx % chartColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Row>
    </Rows>
  );
};

interface IDimensionInfo {
  name: string;
  idx: number;
}

const QueryResultDimensionChart = ({
  items,
  dimensionNames,
}: {
  items: IQueryResultItem[];
  dimensionNames: string[];
}): React.ReactElement | null => {
  const [selectedDim, setSelectedDim] = useState(0);
  const { t } = usePublicaTranslation();

  if (items.length === 0 || dimensionNames.length === 0) return null;

  const validDimensions: IDimensionInfo[] = dimensionNames
    .map((name, idx) => {
      const values = items.map((item) => {
        const parts = item.context.split(',').map((p) => p.trim());
        return parts[idx] || '';
      });
      const uniqueNonEmpty = [...new Set(values.filter(Boolean))];
      const allNumeric = uniqueNonEmpty.every((v) => /^\d+$/.test(v));
      if (uniqueNonEmpty.length < 2 || allNumeric) return null;
      return { name, idx };
    })
    .filter((d): d is IDimensionInfo => d !== null);

  if (validDimensions.length === 0) return null;

  const activeDimIdx = Math.min(selectedDim, validDimensions.length - 1);
  const activeDim = validDimensions[activeDimIdx];

  const aggregated = new Map<string, number>();
  for (const item of items) {
    const parts = item.context.split(',').map((p) => p.trim());
    const dimValue = parts[activeDim.idx] || t('EmptyWithParentheses');
    aggregated.set(dimValue, (aggregated.get(dimValue) || 0) + getValueFromItem(item));
  }

  const chartData: IChartDataPoint[] = Array.from(aggregated.entries())
    .map(([name, value]) => ({
      name: name || t('EmptyWithParentheses'),
      value: Math.round(value * 100) / 100,
    }))
    .sort((a, b) => b.value - a.value);

  const maxLabelLen = Math.max(...chartData.map((d) => d.name.length));
  const leftMargin = Math.min(maxLabelLen * 6.5, 180);

  return (
    <Rows style={{ marginTop: '12px' }}>
      {validDimensions.length > 1 && (
        <Row noPadding marginBottom='8px' displayFlex style={{ flexWrap: 'wrap', gap: '6px' }}>
          {validDimensions.map((dim, idx) => (
            <Chip
              key={dim.name}
              size='small'
              label={formatDimensionName(dim.name)}
              onClick={() => setSelectedDim(idx)}
              clickable
              variant={idx === activeDimIdx ? 'default' : 'outlined'}
              color={idx === activeDimIdx ? 'primary' : 'default'}
              style={{ fontSize: '11px' }}
            />
          ))}
        </Row>
      )}
      <Row noPadding>
        <ResponsiveContainer width='100%' height={chartData.length * 36 + 40}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 4, right: 20, left: leftMargin, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={false} />
            <XAxis type='number' tick={{ fontSize: 11 }} />
            <YAxis
              type='category'
              dataKey='name'
              tick={{ fontSize: 11, fill: publicaColors.darkGray }}
              width={leftMargin}
            />
            <Tooltip
              contentStyle={{
                fontSize: '12px',
                borderRadius: '6px',
                border: `1px solid ${publicaColors.lightGray}`,
              }}
              formatter={(value: number) => [value.toFixed(2), '']}
            />
            <Bar dataKey='value' radius={[0, 4, 4, 0]} barSize={20}>
              {chartData.map((_, idx) => (
                <Cell key={idx} fill={chartColors[idx % chartColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Row>
    </Rows>
  );
};

export interface IPresetQuestion {
  labelTKey: TKey;
  question: string;
  fetchResults?: boolean;
}

export interface IGenieAssistantHandle {
  ask: (question: string, displayText?: string, fetchResults?: boolean) => void;
  openPanel: () => void;
}

interface IGenieAssistantProps {
  presetQuestions?: IPresetQuestion[];
  publisherId?: number;
  siteId?: number;
}

export const getPresetQuestions = (
  _publisherId: number,
  dateRangeLabel: string,
): IPresetQuestion[] => {
  const timeRange = dateRangeLabel.toLowerCase();
  return [
    {
      labelTKey: 'SmartFloorsAssistantTopPerformingContextByRevenue',
      question: `In the ${timeRange}, show the top 10 context combinations (bidder_type, device_type, content_channel) ranked by total_revenue. total_revenue = SUM(revenue_per_request * event_bid_won). Return ONLY a JSON array with objects having keys "context" (string like "bidder_type, device_type, content_channel") and "total_revenue" (number). Then after the JSON, add a brief text summary. Return Both the JSON and the text summary. DO NOT ask any follow up questions.`,
      fetchResults: true,
    },
    {
      labelTKey: 'SmartFloorsAssistantTopDemandSources',
      question: `What top 10 bidder_types have the highest resp_cpm in the ${timeRange}? DO NOT ask any follow up questions.`,
    },
    {
      labelTKey: 'SmartFloorsAssistantAnalyzeFloorDecisions',
      question: `Why did the optimal floor change in the ${timeRange}? DO NOT ask any follow up questions.`,
    },
    {
      labelTKey: 'SmartFloorsAssistantAnalyzeControlVsSmart',
      question: `Show me bidder metrics and decisions where smart is underperforming or overperforming control in the ${timeRange} DO NOT ask any follow up questions.`,
    },
    {
      labelTKey: 'SmartFloorsAssistantTopContentChannels',
      question: `What top 10 content_channels have the highest average resp_cpm_per_second and their avg bid floors in the ${timeRange}? DO NOT ask any follow up questions.`,
    },
  ];
};

interface IMessage {
  role: 'user' | 'assistant';
  content: string;
  attachments?: IGenieAttachment[];
  error?: string;
}

const markdownStyles = `
  .genie-md p { margin: 4px 0; }
  .genie-md h1, .genie-md h2, .genie-md h3 { margin: 8px 0 4px; }
  .genie-md h1 { font-size: 16px; }
  .genie-md h2 { font-size: 14px; }
  .genie-md h3 { font-size: 13px; }
  .genie-md ul, .genie-md ol { margin: 4px 0; padding-left: 20px; }
  .genie-md li { margin: 2px 0; }
  .genie-md code {
    background: ${publicaColors.veryLightGray};
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 12px;
  }
  .genie-md pre {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 11px;
    overflow-x: auto;
    line-height: 1.5;
  }
  .genie-md pre code {
    background: none;
    padding: 0;
    font-size: 11px;
  }
  .genie-md table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin: 8px 0;
  }
  .genie-md th {
    text-align: left;
    padding: 6px 10px;
    border-bottom: 2px solid ${publicaColors.lightGray};
    background: ${publicaColors.veryLightGray};
    font-weight: 600;
    white-space: nowrap;
  }
  .genie-md td {
    padding: 5px 10px;
    border-bottom: 1px solid ${publicaColors.lightGray};
    white-space: nowrap;
  }
  .genie-md tr:nth-child(even) { background: ${publicaColors.veryLightGray}; }
  .genie-md blockquote {
    border-left: 3px solid ${publicaColors.lightGray};
    margin: 4px 0;
    padding: 2px 12px;
    color: ${publicaColors.mediumGray};
  }
  .genie-md strong { font-weight: 600; }
`;

const MarkdownContent = ({ content }: { content: string }): React.ReactNode => (
  <Div className='genie-md' style={{ fontSize: '13px', lineHeight: '1.6' }}>
    <style>{markdownStyles}</style>
    <Markdown>{content}</Markdown>
  </Div>
);

const AttachmentTextRender = ({
  attachment,
}: {
  attachment: IGenieAttachment;
}): React.ReactNode => {
  if (attachment.text) {
    return <MarkdownContent content={attachment.text.content} />;
  }
  return null;
};

const MessageChartRender = ({
  attachments,
}: {
  attachments: IGenieAttachment[];
}): React.ReactNode => {
  for (const att of attachments) {
    if (att.query?.query && att.query_result) {
      const items = parseQueryResultItems(att.query_result);
      const dimensionNames = extractDimensionNames(att.query.query);
      if (items.length > 0 && dimensionNames.length > 0) {
        return <QueryResultDimensionChart items={items} dimensionNames={dimensionNames} />;
      }
      const fallbackChart = parseQueryResultChart(att.query_result);
      if (fallbackChart.length > 0) {
        return <GenieChart data={fallbackChart} />;
      }
    }
  }

  for (const att of attachments) {
    if (att.text) {
      const contextEntries = parseContextData(att.text.content);
      if (contextEntries.length > 0) {
        return <GenieContextChart entries={contextEntries} />;
      }
      const chartData = parseChartData(att.text.content);
      if (chartData.length > 0) {
        return <GenieChart data={chartData} />;
      }
    }
  }

  return null;
};

export const GenieAssistant = React.forwardRef<IGenieAssistantHandle, IGenieAssistantProps>(
  (props, ref) => {
    const { presetQuestions = [], publisherId, siteId } = props;
    const { t } = usePublicaTranslation();
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { user } = useBaseStore();
    const isSuperUser = hasRoleSuperUser(user.user);

    const handleAsk = async (
      question: string,
      displayText?: string,
      fetchResults?: boolean,
    ): Promise<void> => {
      if (!question.trim() || isLoading) return;

      setIsPanelOpen(true);
      setInputValue('');
      setMessages((prev) => [...prev, { role: 'user', content: displayText || question }]);
      setIsLoading(true);

      try {
        const response: IGenieResponse = await askGenie(
          question,
          fetchResults,
          publisherId,
          siteId,
        );
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: response.error || '',
            attachments: response.attachments,
            error: response.error,
          },
        ]);
      } catch (e) {
        const errorMsg = t('SmartFloorsAssistantError');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: errorMsg,
            error: errorMsg,
          },
        ]);
      } finally {
        setIsLoading(false);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    };

    useImperativeHandle(ref, () => ({
      ask: handleAsk,
      openPanel: () => setIsPanelOpen(true),
    }));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleAsk(inputValue);
      }
    };

    const autoResize = (el: HTMLTextAreaElement): void => {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    };

    return (
      <>
        {/* Side Panel */}
        <Div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: isPanelOpen ? '480px' : '0',
            height: '100vh',
            backgroundColor: publicaColors.white,
            boxShadow: isPanelOpen ? '-4px 0 24px rgba(0,0,0,0.12)' : 'none',
            transition: 'width 0.3s ease',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Panel Header */}
          <Row noPadding>
            <Cols
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${publicaColors.lightGray}`,
              }}
            >
              <Col displayFlex style={{ alignItems: 'center', gap: '8px' }}>
                <IconV2 name='ai-icon' small />
                <Text style={{ fontWeight: 600, fontSize: '14px' }} tKey='SmartFloorsAssistant' />
              </Col>
              <Col
                displayFlex
                onClick={() => setIsPanelOpen(false)}
                style={{ cursor: 'pointer', padding: '4px' }}
              >
                <IconV2 name='close' small />
              </Col>
            </Cols>
          </Row>

          {/* Messages */}
          <Div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 20px',
            }}
          >
            {messages.length === 0 && !isLoading && (
              <Div
                style={{
                  textAlign: 'center',
                  color: publicaColors.mediumGray,
                  fontSize: '13px',
                  marginTop: '40px',
                }}
              >
                {t('SmartFloorsAssistantAskQuestion')}
              </Div>
            )}

            {messages.map((msg, idx) => (
              <Rows key={idx} style={{ marginBottom: '16px' }}>
                <Row
                  noPadding
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: publicaColors.mediumGray,
                      marginBottom: '4px',
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    {msg.role === 'user' ? t('You') : t('Assistant')}
                  </Span>
                  <Div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      maxWidth: '100%',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      backgroundColor:
                        msg.role === 'user' ? publicaColors.eyeBlue : publicaColors.veryLightGray,
                      color: publicaColors.darkGray,
                    }}
                  >
                    {msg.role === 'user' && msg.content}
                    {msg.role === 'assistant' && msg.error && !msg.attachments?.length && (
                      <Span style={{ color: publicaColors.red }}>{msg.error}</Span>
                    )}
                    {msg.role === 'assistant' &&
                      msg.attachments?.map((attachment, aIdx) => (
                        <AttachmentTextRender key={`text-${aIdx}`} attachment={attachment} />
                      ))}
                    {msg.role === 'assistant' && msg.attachments && msg.attachments.length > 0 && (
                      <MessageChartRender attachments={msg.attachments} />
                    )}
                    {msg.role === 'assistant' && !msg.error && !msg.attachments?.length && (
                      <Span>{msg.content || t('SmartFloorsAssistantNoResults')}</Span>
                    )}
                  </Div>
                </Row>
              </Rows>
            ))}

            {isLoading && (
              <Div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  backgroundColor: publicaColors.veryLightGray,
                  borderRadius: '10px',
                  fontSize: '13px',
                  color: publicaColors.mediumGray,
                  width: 'fit-content',
                }}
              >
                <Span className='genie-loading-dots'>{t('SmartFloorsAssistantThinking')}</Span>
                <style>{`
                @keyframes genieDots {
                  0%, 20% { content: '.'; }
                  40% { content: '..'; }
                  60%, 100% { content: '...'; }
                }
                .genie-loading-dots::after {
                  content: '';
                  animation: genieDots 1.5s infinite;
                }
              `}</style>
              </Div>
            )}

            <div ref={messagesEndRef} />
          </Div>

          {/* Panel Input */}
          <Rows
            style={{
              padding: '12px 20px',
              borderTop: `1px solid ${publicaColors.lightGray}`,
              flexShrink: 0,
            }}
          >
            {presetQuestions.length > 0 && (
              <Row
                noPadding
                marginBottom='6px'
                displayFlex
                style={{ flexWrap: 'wrap', gap: '6px' }}
              >
                {presetQuestions.map((pq, idx) => (
                  <Chip
                    key={idx}
                    size='small'
                    label={t(pq.labelTKey)}
                    onClick={() => void handleAsk(pq.question, t(pq.labelTKey), pq.fetchResults)}
                    clickable
                    disabled={isLoading}
                    variant='outlined'
                    style={{ fontSize: '11px' }}
                  />
                ))}
              </Row>
            )}
            {isSuperUser && (
              <Row noPadding displayFlex style={{ gap: '8px', alignItems: 'flex-end' }}>
                <Col style={{ flex: 1 }}>
                  <textarea
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      autoResize(e.target);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={t('SmartFloorsAssistantPlaceholder')}
                    disabled={isLoading}
                    rows={1}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: `1px solid ${publicaColors.lightGray}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                      lineHeight: '1.4',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                    }}
                  />
                </Col>
                <Col>
                  <Chip
                    label={t('SmartFloorsAssistantSend')}
                    onClick={() => void handleAsk(inputValue)}
                    clickable
                    disabled={isLoading || !inputValue.trim()}
                    color='primary'
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  />
                </Col>
              </Row>
            )}
          </Rows>
        </Div>
      </>
    );
  },
);
